
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Index from "./pages/Index"; // Changed to direct import

// Lazy load all other routes
const Login = lazy(() => import("./pages/Login"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Disclaimer = lazy(() => import("./pages/legal/Disclaimer"));
const AffiliateDisclosure = lazy(() => import("./pages/legal/AffiliateDisclosure"));
const About = lazy(() => import("./pages/About"));
const FoundationalConcepts = lazy(() => import("./pages/articles/FoundationalConcepts"));
const JourneyStages = lazy(() => import("./pages/articles/JourneyStages"));
const SignsConnections = lazy(() => import("./pages/articles/SignsConnections"));
const SpiritualEsoteric = lazy(() => import("./pages/articles/SpiritualEsoteric"));
const Article = lazy(() => import("./pages/articles/Article"));
const ArticleGenerator = lazy(() => import("./components/admin/ArticleGenerator"));
const CompatibilityQuiz = lazy(() => import("./components/tools/CompatibilityQuiz"));
const StageIdentifier = lazy(() => import("./components/tools/StageIdentifier"));
const LifePathCalculator = lazy(() => import("./components/tools/LifePathCalculator"));
const PastLifeConnection = lazy(() => import("./components/tools/PastLifeConnection"));
const TelepathyAssessment = lazy(() => import("./components/tools/TelepathyAssessment"));
const DreamInterpretationTool = lazy(() => import("./components/tools/DreamInterpretation"));
const ReunionReadiness = lazy(() => import("./components/tools/ReunionReadiness"));
const AstrologyMatch = lazy(() => import("./components/tools/AstrologyMatch"));
const ManifestationReadiness = lazy(() => import("./components/tools/ManifestationReadiness"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (renamed from cacheTime)
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <div className="min-h-screen bg-background flex flex-col">
          <Navbar />
          <main className="flex-grow pt-16">
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
              </div>
            }>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/admin/login" element={<Login />} />
                <Route path="/login" element={<Navigate to="/admin/login" replace />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
                <Route path="/about" element={<About />} />
                <Route path="/foundational-concepts" element={<FoundationalConcepts />} />
                <Route path="/journey-stages" element={<JourneyStages />} />
                <Route path="/signs-connections" element={<SignsConnections />} />
                <Route path="/spiritual-esoteric" element={<SpiritualEsoteric />} />
                <Route path="/admin/article-generator" element={<ArticleGenerator />} />
                <Route path="/tools/compatibility-quiz" element={<CompatibilityQuiz />} />
                <Route path="/tools/stage-identifier" element={<StageIdentifier />} />
                <Route path="/tools/life-path-calculator" element={<LifePathCalculator />} />
                <Route path="/tools/past-life" element={<PastLifeConnection />} />
                <Route path="/tools/telepathy" element={<TelepathyAssessment />} />
                <Route path="/tools/dream-interpretation" element={<DreamInterpretationTool />} />
                <Route path="/tools/reunion-readiness" element={<ReunionReadiness />} />
                <Route path="/tools/astrology-match" element={<AstrologyMatch />} />
                <Route path="/tools/manifestation" element={<ManifestationReadiness />} />
                <Route path="/:title" element={<Article />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
